import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  locationsService,
  type CitiesByState,
  type CustomCityRow,
} from '@/services/locations.service';

const CUSTOM_CITIES_KEY = ['custom-cities'];
const ALL_CUSTOM_CITIES_KEY = ['custom-cities', 'all'];

export const useCustomCities = (enabled = true) =>
  useQuery<CitiesByState>({
    queryKey: CUSTOM_CITIES_KEY,
    queryFn: locationsService.getCustomCities,
    staleTime: 5 * 60 * 1000,
    enabled,
  });

export const useAddCustomCity = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ state, city }: { state: string; city: string }) =>
      locationsService.addCustomCity(state, city),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CUSTOM_CITIES_KEY }),
  });
};

// ─── Admin moderation ──────────────────────────────────────────────────────

export const useAllCustomCities = () =>
  useQuery<CustomCityRow[]>({
    queryKey: ALL_CUSTOM_CITIES_KEY,
    queryFn: locationsService.listAllCustomCities,
  });

// Both the admin list and the dropdown's grouped list are stale after a
// rename/delete, so invalidate the whole custom-cities key tree.
export const useUpdateCustomCity = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, city }: { id: number; city: string }) =>
      locationsService.updateCustomCity(id, city),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CUSTOM_CITIES_KEY }),
  });
};

export const useDeleteCustomCity = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => locationsService.deleteCustomCity(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CUSTOM_CITIES_KEY }),
  });
};
